import {
	format,
	intervalToDuration,
	isValid,
	isBefore,
	isEqual,
	isAfter,
	differenceInMinutes,
	differenceInDays,
} from "date-fns";
import DOMPurify from "dompurify";
import Cookies from "js-cookie";
import pick from "lodash/pick";

import { GeneralStatus } from "utility/enums/general-status";
import Emitter from "./emitter";
import * as Yup from "yup";

import {
	circleOutlineDefaultIcon,
	circleOutlinePrimaryIcon,
	deleteIcon,
} from "assets/images";
import { IOption } from "./interfaces/select-option";
import {
	ReqConversionToBidActionType,
	RequisitionActionType,
	RequisitionConfirmationType,
} from "./enums/requisition-status";
import { SchedulerEventConfirmationType } from "./enums/bid-meeting-event";
import {
	BudgetAllocationActionType,
	BudgetAllocationConfirmationType,
} from "./enums/budget-allocation";
import {
	OPEN_SESSION_EXPIRE_DIALOG,
	SHOW_LOADER,
	SHOW_SNACKBAR_MESSAGE,
} from "./emitterEvents";
import { UserType } from "./enums/user-type";
import { IBaseToken, TokenStatus } from "models/LoginUserDetail";
import { AuthService } from "services/auth.service";
import {
	CellphoneCode,
	ExternalUserConfirmationType,
} from "./enums/external-user";
import { BidActionType, BidConfirmationType } from "./enums/bid-detail";
import {
	ContractActionType,
	ContractConfirmationType,
} from "./enums/contract-detail";
import { BidCommitteeConfirmationType } from "./enums/bid-committee-status";
import { ItemConfirmationType } from "./enums/item-status";
import { BidQueryConfirmationType } from "components/SupplyChainManagementPortal/BidQuery/EmailBidQueryConfirmation/BidQueryConfirmationMaster";
import {
	DynamicCalculationStepRow,
	DynamicCalculationSteps,
	IOrganizationEvaluationRHSEquationDetail,
} from "models/EquationModel";

export class Shared {
	public static showLoader(loading: boolean) {
		Emitter.emit(SHOW_LOADER, loading);
	}

	public static showSnackbarMessage(
		message: React.ReactNode | string,
		severityType?: string
	) {
		Emitter.emit(SHOW_SNACKBAR_MESSAGE, { message, severityType });
	}

	public static defaultPageSize: number = 10;

	public static defaultMaskValue = "_";

	// editor API key is defferent for ACSA domain, Used registered email is "helpdesk@sethewo.co.za"
	// Currently approved domain list for editor is "https://scm-preprod.azure.airports.co.za/" and "https://scm-preprod.airports.co.za/".
	public static editorAPIkey =
		"sk3bc3t00gcfj8t61aqpyzx29w8qcca8foz5ss3cdwtqtlz0";

	public static getFormattedDate(dateValue: Date, dateFormat: string) {
		return dateValue && isValid(new Date(dateValue))
			? format(new Date(dateValue), dateFormat)
			: "";
	}

	// Restricted msword (.doc), docx, and .txt document types. Allowed: pdf, png, jpg, jpeg
	public static mimeTypeList = [
		// "application/msword",
		// "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
		"application/pdf",
		// "text/plain",
		"image/jpeg",
		"image/png",
		"image/jpg",
		".pdf",
		".png",
		".jpg",
		".jpeg",
	];

	public static imageTypeList = ["image/jpg", "image/jpeg", "image/png"];

	public static excelTypeList = [
		".csv",
		".xls",
		".xlsx",
		"text/csv",
		"application/csv",
		"text/comma-separated-values",
		"application/vnd.ms-excel",
		"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	];

	public static localStorageKeys = {
		RedirectUrl: "redirectUrl",
		userDetails: "userDetails",
		organizationDetails: "organizationDetails",
		lastInteractionTime: "lastInteractionTime",
		resetSessionTimer: "resetSessionTimer",
		isExternalUserLoggedIn: "isExternalUserLoggedIn",
	};

	public static cookieStorageKeys = {
		Token: "authToken",
		RefreshToken: "refreshToken",
		TokenExpiry: "authTokenExpiry",
	};

	// Convert ZAR currency input to number format
	// public static reverseFormatNumber = (value: string, prefix = "R") => {
	// 	if (value) {
	// 		const decimalSeparator = value.replace(",", "."); // Replace decimal seprator of "en-ZA" (",") to (".")
	// 		const thousandSeparator = decimalSeparator.replaceAll(/\s+/g, ""); // Replace thousand seprator of "en-ZA" (" ") to ("") (i.e. remove spaces)
	// 		const convertedValue = thousandSeparator.replace(prefix, ""); // Remove currency symbol
	// 		return convertedValue;
	// 	}
	// 	return "";
	// };
	public static reverseFormatNumber = (value: string, prefix = "R") => {
		if (!value) return "";

		let result = value.toString().trim();

		// Remove prefix (R or any currency)
		if (prefix) {
			result = result.replace(prefix, "");
		}

		// Remove all spaces
		result = result.replace(/\s+/g, "");

		// Case 1: both comma & dot exist → assume comma = thousand separator
		if (result.includes(",") && result.includes(".")) {
			result = result.replace(/,/g, "");
		}

		// Case 2: only comma exists → could be decimal OR thousand
		else if (result.includes(",")) {
			const parts = result.split(",");

			// If last part length is 2 → decimal (e.g. 10000,50)
			if (parts[parts.length - 1].length === 2) {
				result = result.replace(",", ".");
			} else {
				// otherwise treat as thousand separator
				result = result.replace(/,/g, "");
			}
		}

		// Final safety: remove anything non-numeric except dot
		result = result.replace(/[^0-9.]/g, "");

		return result;
	};

	public static currencyFormatter = new Intl.NumberFormat("en-ZA", {
		style: "currency",
		currency: "ZAR",
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});

	public static currencyFormatterWithoutSymbol = new Intl.NumberFormat(
		"en-ZA",
		{ style: "decimal", minimumFractionDigits: 2, maximumFractionDigits: 2 }
	);

	public static Duration = (startDate: Date, endDate: Date): string => {
		startDate.setHours(0, 0, 0, 0);
		endDate.setHours(0, 0, 0, 0);
		if (
			!isValid(startDate) ||
			!isValid(endDate) ||
			isBefore(endDate, startDate) ||
			isEqual(endDate, startDate)
		) {
			return "";
		}
		const duration = intervalToDuration({ start: startDate, end: endDate });
		return `${
			Number(duration.years) > 0
				? duration.years + (duration.years === 1 ? " Year" : " Years")
				: ""
		} ${
			Number(duration.months) > 0
				? duration.months + (duration.months === 1 ? " Month" : " Months")
				: ""
		} ${
			Number(duration.days) > 0
				? duration.days + (duration.days === 1 ? " Day" : " Days")
				: ""
		}`;
	};

	public static DurationWithTime = (startDate: Date, endDate: Date): string => {
		if (
			!isValid(startDate) ||
			!isValid(endDate) ||
			isBefore(endDate, startDate) ||
			isEqual(endDate, startDate)
		) {
			return "";
		}
		const duration = intervalToDuration({ start: startDate, end: endDate });
		const diffInDays = differenceInDays(endDate, startDate);
		if (diffInDays > 0) {
			return `${
				Number(duration.years) > 0
					? duration.years + (duration.years === 1 ? " Year" : " Years")
					: ""
			} ${
				Number(duration.months) > 0
					? duration.months + (duration.months === 1 ? " Month" : " Months")
					: ""
			} ${
				Number(duration.days) > 0
					? duration.days + (duration.days === 1 ? " Day" : " Days")
					: ""
			}`;
		} else {
			return `${
				Number(duration.hours) > 0
					? duration.hours + (duration.hours === 1 ? " Hour" : " Hours")
					: ""
			} ${
				Number(duration.minutes) > 0
					? duration.minutes + (duration.minutes === 1 ? " Minute" : " Minutes")
					: ""
			} ${
				(Number(duration.hours) === 0 || Number(duration.minutes) === 0) &&
				Number(duration.seconds) > 0
					? duration.seconds + (duration.seconds === 1 ? " Second" : " Seconds")
					: ""
			}`;
		}
	};

	public static setPageNumber(
		currentPageNumber: number,
		rowsPerPage: number,
		totalRowCount: number
	) {
		const startIndex = currentPageNumber * rowsPerPage;
		const pageNumber =
			startIndex < totalRowCount
				? Math.ceil(startIndex / rowsPerPage)
				: Math.ceil(totalRowCount / rowsPerPage) - 1;
		return pageNumber < 0 ? 0 : pageNumber;
	}

	public static convertStringToJson(stringValue: string) {
		// Replace \u0022 with " to simplify processing, then split into rows.
		const rows = stringValue.replace(/\\u0022/g, '"').split("\n");
		const headers = rows[0].split(",");
		const jsonArray = [];
		// Local function to split a row into columns
		const splitRow = (row: any) => {
			const columns = [];
			let columnStart = 0;
			let insideQuote = false;
			for (let i = 0; i <= row.length; i++) {
				if (row[i] === '"') {
					insideQuote = !insideQuote;
				} else if (!insideQuote && (row[i] === "," || i === row.length)) {
					const column = row.substring(columnStart, i).trim();
					// Remove leading and trailing quotes, if present
					columns.push(column.replace(/^"|"$/g, ""));
					columnStart = i + 1;
				}
			}
			return columns;
		};
		// Process each row
		for (let i = 1; i < rows.length; i++) {
			// Split the row into columns using the local function
			const columns = splitRow(rows[i]);
			// Create an object for each row
			const obj: any = {};
			for (let j = 0; j < headers.length; j++) {
				obj[headers[j]] = columns[j];
			}
			jsonArray.push(obj);
		}
		return jsonArray;
	}

	public static downloadCSVFile = async (
		responseText: string,
		fileName: string
	) => {
		//Interaction-driven Code Splitting
		//modern JavaScript feature known as a Dynamic Import, to reduce size of main bundle.
		const XLSX = await import("xlsx");
		let ws = null;
		if (
			fileName === "CSD Supplier Maintenance_list" ||
			fileName === "CSD Daily Update_list"
		) {
			ws = XLSX.utils.json_to_sheet(Shared.convertStringToJson(responseText), {
				skipHeader: true,
			});
			const merge = [
				{ s: { r: 0, c: 0 }, e: { r: 3, c: 0 } },
				{ s: { r: 3, c: 3 }, e: { r: 3, c: 5 } },
			];
			ws["!merges"] = merge;
		} else {
			ws = XLSX.utils.json_to_sheet(Shared.convertStringToJson(responseText));
		}

		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
		const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
		const blob = new Blob([excelBuffer], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		});
		const dataURL = window.URL.createObjectURL(blob);
		if (dataURL) {
			if (document) {
				const link = document.createElement("a");
				link.href = dataURL;
				link.download = `${fileName}.xlsx`;
				document.body.appendChild(link); // Required for FF
				link.click();
				document.body.removeChild(link);
			}
			if (window.URL) {
				setTimeout(() => {
					window.URL.revokeObjectURL(dataURL);
				}, 100);
			}
		}
	};

	// Required to format filter list for API call
	public static getFilterValue = (
		value: string[],
		listLength: number
	): string => {
		return value.length === 0
			? "-1"
			: value.length !== listLength
			? value.join(",")
			: "";
	};

	public static isAfterDate = (date1: Date, date2: Date) => {
		return isAfter(
			new Date(new Date(date1).setHours(0, 0, 0, 0)),
			new Date(new Date(date2).setHours(0, 0, 0, 0))
		);
	};

	public static getGeneralStatusIcon = (status: string) => {
		switch (status) {
			case GeneralStatus.Delete:
				return deleteIcon;
			case GeneralStatus.Active:
				return circleOutlinePrimaryIcon;
			case GeneralStatus.InActive:
				return circleOutlineDefaultIcon;
			default:
				break;
		}
	};

	// Get sanitized value to show in grid
	public static getSanitizedValue = (value: string) => {
		return DOMPurify.sanitize(value);
	};

	public static openPreviewAsPDF = (value: string) => {
		if (!value) {
			Shared.showSnackbarMessage("No data available for preview!", "info");
			return;
		}
		const byteCharacters = window.atob(value);
		const byteNumbers = new Array(value.length);
		for (let i = 0; i < byteCharacters.length; i++) {
			byteNumbers[i] = byteCharacters.charCodeAt(i);
		}
		const byteArray = new Uint8Array(byteNumbers);
		const file = new Blob([byteArray], {
			type: "application/pdf;base64",
		});
		const fileURL = URL.createObjectURL(file);
		window.open(fileURL);
	};

	public static numberValidation = (value: string, type = "decimal") => {
		if (!value) return false;
		let regex: RegExp = /^$/;
		if (type === "decimal") {
			regex = /^[0-9]*(\.[0-9]{0,2})?$/;
		} else if (type === "number") {
			regex = /^[0-9]*$/;
		}
		return regex && regex.test(value);
	};

	public static getFormattedCurrency(
		value: number,
		isWithoutSymbol: boolean = false
	) {
		if (isWithoutSymbol) {
			return this.currencyFormatterWithoutSymbol.format(value);
		}
		return this.currencyFormatter.format(value);
	}

	public static sortData = (data: any, columnName: string) => {
		if (data != null) {
			Object.keys(data).sort((a: any, b: any) => {
				const nameA = data[a][columnName];
				const nameB = data[b][columnName];
				if (nameA < nameB) {
					return -1;
				}
				if (nameA > nameB) {
					return 1;
				}
				return 0;
			});
		}
		return data;
	};

	public static getFormattedTileValue(value: number) {
		return new Intl.NumberFormat("en-ZA", {
			style: "currency",
			currency: "ZAR",
			notation: "compact",
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		})
			.format(value)
			.replace(",", ".");
	}

	public static cellphoneCodeList: IOption[] = [
		{ label: "+27", value: "+27" },
		{ label: "+91", value: "+91" },
	];

	public static redirectionRequiredRoutes = [
		...Object.values(RequisitionActionType).map(
			(ra) => `/requisition-entry/${ra}`
		),
		...Object.values(RequisitionConfirmationType).map(
			(rca) => `/requisition/${rca}`
		),
		...Object.values(SchedulerEventConfirmationType).map(
			(sca) => `/system-scheduler/${sca}`
		),
		...Object.values(BudgetAllocationConfirmationType).map(
			(ba) => `/budget-allocation/${ba}`
		),
		...Object.values(BudgetAllocationActionType).map(
			(cba) => `/budget-allocation-detail/${cba}`
		),
		...Object.values(ExternalUserConfirmationType).map(
			(euc) => `/external-user/${euc}`
		),
		...Object.values(ReqConversionToBidActionType).map(
			(rcb) => `/requisition-conversion-to-bid/${rcb}`
		),
		...Object.values(BidActionType).map((be) => `/bid-entry/${be}`),
		"/budget-change-order-detail/",
		...Object.values(ContractActionType).map((ca) => `/contract-entry/${ca}`),
		...Object.values(ContractConfirmationType).map((ca) => `/contract/${ca}`),
		...Object.values(RequisitionActionType).map(
			(ra) => `/contract-po-entry/${ra}`
		),
		...Object.values(RequisitionConfirmationType).map(
			(rca) => `/contract-po/${rca}`
		),
		...Object.values(RequisitionActionType).map(
			(ra) => `/contract-variation-entry/${ra}`
		),
		...Object.values(RequisitionConfirmationType).map(
			(rca) => `/contract-variation/${rca}`
		),
		...Object.values(BidCommitteeConfirmationType).map(
			(bca) => `/bid-committee-member/${bca}`
		),
		...Object.values(BidCommitteeConfirmationType).map(
			(bca) => `/bid-committee/${bca}`
		),
		...Object.values(ItemConfirmationType).map((ica) => `/limited-item/${ica}`),
		...Object.values(BidConfirmationType).map((ica) => `/bid/${ica}`),
		...Object.values(BidQueryConfirmationType).map((bca) => `/query/${bca}`),

		// ...Object.values(ContractConfirmationType).map((bca) => `/contract/${bca}`),
	];

	// convert item list to IOption[], required for drop-downs/auto complete
	public static getDropdownOptions = (
		itemList: any,
		valueProperty: string,
		labelProperty: string = "",
		isConvertValueToString: boolean = false,
		groupByProperty: string = ""
	) => {
		const tempList: IOption[] = [];
		itemList.map((item: any) =>
			tempList.push({
				label: labelProperty ? item[labelProperty] : item.name,
				value: isConvertValueToString
					? item[valueProperty].toString()
					: item[valueProperty],
				groupBy: groupByProperty ? item[groupByProperty] : "",
			})
		);
		return tempList;
	};

	public static getAggregatedAmount = (
		list: any,
		propertyName: string,
		isWithoutCurrencyFormat = false
	) => {
		const totalAmount = list.reduce(
			(total: number, currentValue: any) =>
				(total =
					total +
					(isNaN(currentValue[propertyName]) ? 0 : currentValue[propertyName])),
			0
		);
		return isWithoutCurrencyFormat
			? totalAmount
			: this.getFormattedCurrency(totalAmount);
	};

	public static isFileNameSizeValid = (fileName: string) => {
		return fileName.length <= 100;
	};

	// Internal User Authentication
	public static isInternalUserLoggedIn = () => {
		const userDetails = localStorage.getItem(
			Shared.localStorageKeys.userDetails
		);
		return (
			userDetails && JSON.parse(userDetails).userTypeId === UserType.Internal
		);
	};

	// EXternal User Authentication
	public static isExternalUserLoggedIn = (redirectToHomePage = true) => {
		const userDetails = localStorage.getItem(
			Shared.localStorageKeys.userDetails
		);
		const refreshToken =
			Cookies.get(Shared.cookieStorageKeys.RefreshToken) || "";
		const authToken = Cookies.get(Shared.cookieStorageKeys.Token) || "";
		if ((!userDetails || !refreshToken || !authToken) && redirectToHomePage) {
			Shared.externalUserLogout();
		}
		return (
			userDetails && JSON.parse(userDetails).userTypeId === UserType.External
		);
	};

	public static getExternalUserAuthToken = async () => {
		const authToken = Cookies.get(Shared.cookieStorageKeys.Token) || "";
		const refreshToken =
			Cookies.get(Shared.cookieStorageKeys.RefreshToken) || "";
		const tokenExpireTime =
			Cookies.get(Shared.cookieStorageKeys.TokenExpiry) || "";
		if (!refreshToken || !isValid(new Date(tokenExpireTime))) {
			Shared.externalUserLogout();
		}
		// check auth token expiration time - if expired call refresh token API
		const difference = differenceInMinutes(
			new Date(tokenExpireTime),
			new Date()
		);
		if (difference < 2) {
			try {
				const response = await AuthService.refreshAuthToken(
					authToken,
					refreshToken
				);
				if (
					response &&
					response.token &&
					response.status === TokenStatus.Valid
				) {
					// set token in cookies
					Shared.setCookieValue(
						pick(response, Object.keys(new IBaseToken())) as IBaseToken
					);
					return response.token;
				} else {
					Shared.externalUserLogout();
				}
			} catch (error) {
				Shared.externalUserLogout();
			}
		}
		return authToken;
	};

	public static setCookieValue = (model: IBaseToken) => {
		Cookies.set(Shared.cookieStorageKeys.Token, model.token);
		Cookies.set(Shared.cookieStorageKeys.RefreshToken, model.refreshToken, {
			expires: new Date(model.refreshTokenExpiry),
		});
		Cookies.set(
			Shared.cookieStorageKeys.TokenExpiry,
			model.tokenExpiry.toString()
		);
	};

	public static externalUserLogout = () => {
		Emitter.emit(OPEN_SESSION_EXPIRE_DIALOG, undefined);
	};
	// EXternal User Authentication - END

	public static clearStorageValues = (removeCookies = true) => {
		localStorage.clear();
		if (removeCookies) {
			Cookies.remove(Shared.cookieStorageKeys.Token);
			Cookies.remove(Shared.cookieStorageKeys.RefreshToken);
			Cookies.remove(Shared.cookieStorageKeys.TokenExpiry);
		}
	};

	public static telephoneNumberValidation = () => {
		return {
			cellphoneNumber: Yup.string()
				.nullable()
				.required("Please enter a cellphone number")
				.matches(/^[0-9]+$/, "Only numbers are allowed")
				.when("cellphoneCode", {
					is: (cellphoneCode: string) => cellphoneCode === CellphoneCode.SA,
					then: (schema) => {
						return schema.length(9, "Cellphone number must be 9 digits long");
					},
					otherwise: (schema) =>
						schema.length(10, "Cellphone number must be 10 digits long"),
				}),
			contactNumber: Yup.string()
				.nullable()
				.required("Please enter a contact number")
				.matches(/^[0-9]+$/, "Only numbers are allowed")
				.when("contactNumberCode", {
					is: (contactNumberCode: string) =>
						contactNumberCode === CellphoneCode.SA,
					then: (schema) => {
						return schema.length(9, "Contact number must be 9 digits long");
					},
					otherwise: (schema) =>
						schema.length(10, "Contact number must be 10 digits long"),
				}),
			telephoneNumber: Yup.string()
				.nullable()
				.required("Please enter a telephone number")
				.matches(/^[0-9]+$/, "Only numbers are allowed")
				.length(7, "Telephone number must be 7 digits long"),
			telephoneCode: Yup.string()
				.nullable()
				.required("Please enter a dialing code")
				.matches(/^[0-9]+$/, "Only numbers are allowed")
				.length(3, "Dialing code must be 3 digits long"),
		};
	};

	// remove class from DOM if fly-in panel is open
	public static hideFlyInPanel = () => {
		if (document.body.classList.contains("show-item-selection"))
			document.body.classList.remove("show-item-selection");
		if (document.body.classList.contains("show-steps"))
			document.body.classList.remove("show-steps");
	};

	public static formatDate = (dateString: string): string => {
		const date = new Date(dateString);

		const day = date.getDate();
		const monthNames = [
			"January",
			"February",
			"March",
			"April",
			"May",
			"June",
			"July",
			"August",
			"September",
			"October",
			"November",
			"December",
		];
		const month = monthNames[date.getMonth()];
		const year = date.getFullYear();

		const getOrdinalSuffix = (n: number): string => {
			if (n > 3 && n < 21) return "th"; // Special case for 11th-13th
			switch (n % 10) {
				case 1:
					return "st";
				case 2:
					return "nd";
				case 3:
					return "rd";
				default:
					return "th";
			}
		};

		return `${day}${getOrdinalSuffix(day)} of ${month} ${year}`;
	};

	public static getFormattedHour = (time: string) => {
		const [hour, minute] = time.split(":").map(Number);
		const period = hour >= 12 ? "PM" : "AM";
		const formattedHour = hour % 12 || 12;
		return `${formattedHour}:${minute.toString().padStart(2, "0")} ${period}`;
	};

	public static getFormattedNumberValue = (value: number): string => {
		if (value >= 1_000_000_000) {
			return (value / 1_000_000_000).toFixed(2) + "B";
		} else if (value >= 1_000_000) {
			return (value / 1_000_000).toFixed(2) + "M";
		} else if (value >= 100_000) {
			return (value / 1_000).toFixed(2) + "K";
		} else if (value >= 1_000) {
			return (value / 1_000).toFixed(2) + "K";
		} else {
			return value.toFixed(2);
		}
	};

	public static getVariableMap = (summary: {
		pt: number;
		pmin: number;
		pmax: number;
		pavg: number;
	}) => ({
		1: { symbol: "Pₜ", name: "Bidder's Price", value: summary.pt },
		2: { symbol: "Pₘᵢₙ", name: "Lowest Price", value: summary.pmin },
		3: { symbol: "Pₘₐₓ", name: "Maximum Price", value: summary.pmax },
		4: { symbol: "Pₐᵥg", name: "Average Price", value: summary.pavg },
	});

	public static computeDynamicSteps = (
		rsModels: IOrganizationEvaluationRHSEquationDetail[],
		summaryValues: {
			pt: number;
			pmin: number;
			pmax: number;
			pavg: number;
			total?: number;
		},
		lhsSymbol: string = "Ps",
		formatNumber: (val: number, decimals?: number) => string
	): DynamicCalculationSteps => {
		const vars = this.getVariableMap(summaryValues);

		// 1. Sort RHS tokens by sequenceNumber
		const sortedRhs = [...rsModels].sort(
			(a, b) => a.sequenceNumber - b.sequenceNumber
		);

		// 2. Find Points Constant (First numeric constant token, e.g. 80 or 90)
		const pointsToken = sortedRhs.find((m) => m.equationFieldId === 1);
		const points = pointsToken ? Number(pointsToken.value) : 80;

		// 3. Define Token Data Structures
		type TokenType = "NUMBER" | "OPERATOR" | "BRACKET";
		interface Token {
			type: TokenType;
			value: string;
			numVal?: number;
		}

		// Map API RHS models to initial substituted Token array
		const initialTokens: Token[] = sortedRhs.map((item) => {
			if (
				item.equationVariableId &&
				vars[item.equationVariableId as keyof typeof vars]
			) {
				const val = vars[item.equationVariableId as keyof typeof vars].value;
				return {
					type: "NUMBER",
					value: formatNumber(val, 2),
					numVal: val,
				};
			}

			const strVal = String(item.value ?? "").trim();

			if (
				strVal === "(" ||
				strVal === ")" ||
				strVal === "[" ||
				strVal === "]"
			) {
				return { type: "BRACKET", value: strVal };
			}

			if (strVal === "/" || strVal === "÷") {
				return { type: "OPERATOR", value: "÷" };
			}
			if (strVal === "*" || strVal === "×") {
				return { type: "OPERATOR", value: "×" };
			}
			if (strVal === "+" || strVal === "-") {
				return { type: "OPERATOR", value: strVal };
			}

			const parsedNum = Number(strVal);
			if (!isNaN(parsedNum) && strVal !== "") {
				return {
					type: "NUMBER",
					value: formatNumber(parsedNum, parsedNum % 1 !== 0 ? 2 : 0),
					numVal: parsedNum,
				};
			}

			return { type: "OPERATOR", value: strVal };
		});

		// Helper to format tokens to clean string
		const tokensToString = (toks: Token[]): string => {
			const parts: string[] = [];
			for (let i = 0; i < toks.length; i++) {
				const tok = toks[i];
				const prev = toks[i - 1];

				if (
					tok.type === "NUMBER" &&
					tok.numVal !== undefined &&
					tok.numVal < 0 &&
					prev &&
					prev.type === "OPERATOR"
				) {
					parts.push(`(${tok.value})`);
				} else {
					parts.push(tok.value);
				}
			}
			return parts.join(" ");
		};

		// Helper for single operation calculation
		const reduceSingleOp = (left: Token, op: string, right: Token): Token => {
			const numA = left.numVal ?? 0;
			const numB = right.numVal ?? 0;

			if ((op === "÷" || op === "/") && numB === 0) {
				return { type: "NUMBER", value: "-", numVal: NaN };
			}

			let res = 0;
			if (op === "÷" || op === "/") {
				res = numA / numB;
			} else if (op === "×" || op === "*") {
				res = numA * numB;
			} else if (op === "+") {
				res = numA + numB;
			} else if (op === "-") {
				res = numA - numB;
			}

			let decimals = 2;
			if (res % 1 !== 0) {
				const absVal = Math.abs(res);
				if (absVal < 0.01 && absVal > 0) {
					decimals = 6;
				} else if (absVal < 1) {
					decimals = 6;
				}
			} else {
				decimals = 2;
			}

			return {
				type: "NUMBER",
				value: formatNumber(res, decimals),
				numVal: res,
			};
		};

		// Helper to reduce slice of tokens by BODMAS priority
		const reduceSlice = (
			slice: Token[]
		): { newSlice: Token[]; performed: boolean } => {
			if (slice.length < 3) return { newSlice: slice, performed: false };

			// Unary minus at start of slice (e.g. - 200)
			if (slice[0].value === "-" && slice[1].type === "NUMBER") {
				const negVal = -(slice[1].numVal ?? 0);
				const newToken: Token = {
					type: "NUMBER",
					value: formatNumber(negVal, 2),
					numVal: negVal,
				};
				return { newSlice: [newToken, ...slice.slice(2)], performed: true };
			}

			// Priority 1: Division (÷) and Multiplication (×)
			for (let i = 0; i < slice.length; i++) {
				const val = slice[i].value;
				if (
					slice[i].type === "OPERATOR" &&
					(val === "÷" || val === "×" || val === "/" || val === "*")
				) {
					if (
						i > 0 &&
						i < slice.length - 1 &&
						slice[i - 1].type === "NUMBER" &&
						slice[i + 1].type === "NUMBER"
					) {
						const newToken = reduceSingleOp(slice[i - 1], val, slice[i + 1]);
						const resSlice = [
							...slice.slice(0, i - 1),
							newToken,
							...slice.slice(i + 2),
						];
						return { newSlice: resSlice, performed: true };
					}
				}
			}

			// Priority 2: Addition (+) and Subtraction (-)
			for (let i = 0; i < slice.length; i++) {
				const val = slice[i].value;
				if (slice[i].type === "OPERATOR" && (val === "+" || val === "-")) {
					if (
						i > 0 &&
						i < slice.length - 1 &&
						slice[i - 1].type === "NUMBER" &&
						slice[i + 1].type === "NUMBER"
					) {
						const newToken = reduceSingleOp(slice[i - 1], val, slice[i + 1]);
						const resSlice = [
							...slice.slice(0, i - 1),
							newToken,
							...slice.slice(i + 2),
						];
						return { newSlice: resSlice, performed: true };
					}
				}
			}

			return { newSlice: slice, performed: false };
		};

		// Pass-by-pass reduction function
		const reduceTokensOnePass = (currentTokens: Token[]): Token[] => {
			let tokens = [...currentTokens];

			// Strip any single-token brackets e.g. ( 300 000,00 ) -> 300 000,00
			let changed = true;
			while (changed) {
				changed = false;
				for (let i = 0; i < tokens.length - 2; i++) {
					if (
						(tokens[i].value === "(" || tokens[i].value === "[") &&
						tokens[i + 1].type === "NUMBER" &&
						(tokens[i + 2].value === ")" || tokens[i + 2].value === "]")
					) {
						tokens.splice(i, 3, tokens[i + 1]);
						changed = true;
						break;
					}
				}
			}

			if (tokens.length <= 1) return tokens;

			// Find innermost bracket pair
			let innermostOpen = -1;
			let innermostClose = -1;

			for (let i = 0; i < tokens.length; i++) {
				if (tokens[i].value === "(" || tokens[i].value === "[") {
					innermostOpen = i;
				} else if (
					(tokens[i].value === ")" || tokens[i].value === "]") &&
					innermostOpen !== -1
				) {
					innermostClose = i;
					break;
				}
			}

			if (innermostOpen !== -1 && innermostClose !== -1) {
				const inside = tokens.slice(innermostOpen + 1, innermostClose);
				const { newSlice, performed } = reduceSlice(inside);

				if (performed) {
					if (newSlice.length === 1 && newSlice[0].type === "NUMBER") {
						tokens.splice(
							innermostOpen,
							innermostClose - innermostOpen + 1,
							newSlice[0]
						);
					} else {
						tokens.splice(
							innermostOpen + 1,
							innermostClose - innermostOpen - 1,
							...newSlice
						);
					}
					return tokens;
				}
			} else {
				// No brackets, reduce at top level
				const { newSlice, performed } = reduceSlice(tokens);
				if (performed) {
					return newSlice;
				}
			}

			return tokens;
		};

		// Execute multi-pass BODMAS loop
		const allSteps: DynamicCalculationStepRow[] = [];
		let currentTokens = [...initialTokens];
		let stepCount = 1;

		// Initial Substituted Step 1
		const initialExpr = tokensToString(currentTokens);
		allSteps.push({
			stepNumber: stepCount++,
			label: "=",
			expression: initialExpr,
		});

		let lastExpr = initialExpr;
		let maxPasses = 20;

		while (currentTokens.length > 1 && maxPasses > 0) {
			maxPasses--;
			const nextTokens = reduceTokensOnePass(currentTokens);

			// Check if tokens changed
			const nextExpr = tokensToString(nextTokens);
			if (nextExpr === lastExpr) {
				// No further reduction possible
				break;
			}

			allSteps.push({
				stepNumber: stepCount++,
				label: "=",
				expression: nextExpr,
				numericValue:
					nextTokens.length === 1 && nextTokens[0].numVal !== undefined
						? nextTokens[0].numVal
						: undefined,
			});

			currentTokens = nextTokens;
			lastExpr = nextExpr;
		}

		// Calculate bracket ratio for fallback total
		const pt = summaryValues.pt || 0;
		const pmin = summaryValues.pmin || 0;
		const diff = pt - pmin;
		const ratio = pmin > 0 ? diff / pmin : 0;
		const bracketResult = 1 - ratio;

		const calculatedFinal =
			currentTokens.length === 1 && currentTokens[0].numVal !== undefined
				? currentTokens[0].numVal
				: points * bracketResult;

		const finalScore =
			// summaryValues.total !== undefined && summaryValues.total !== null
			// 	? summaryValues.total
			// 	:
			calculatedFinal;

		return {
			points,
			step1Substituted: allSteps[0]?.expression || "",
			step2Simplified: allSteps[1]?.expression || allSteps[0]?.expression || "",
			bracketResult,
			finalScore,
			lhsSymbol,
			allSteps,
		};
	};
}
